import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/src/lib/components/ui/button";
import { presentAppError } from "@/src/lib/errors/present-app-error";
import { reportClientError } from "@/src/lib/utils/report-client-error";

interface ErrorBoundaryProps {
	children: ReactNode;
	fallback?: ReactNode;
	onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = {
			hasError: false,
			error: null,
		};
	}

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return {
			hasError: true,
			error,
		};
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		reportClientError(error, {
			componentStack: errorInfo.componentStack,
			source: "ErrorBoundary",
		});
		this.props.onError?.(error, errorInfo);
	}

	render(): ReactNode {
		if (this.state.hasError) {
			if (this.props.fallback) {
				return this.props.fallback;
			}

			const presented = this.state.error
				? presentAppError(this.state.error)
				: null;

			return (
				<div className="relative overflow-hidden rounded-lg border border-destructive/20 bg-destructive/5 p-4 shadow-sm animate-in fade-in duration-300">
					{/* Animated gradient line */}
					<div className="absolute inset-x-0 top-0 h-0.5 bg-linear-to-r from-transparent via-destructive to-transparent animate-shimmer" />

					<div className="flex items-start space-x-3">
						<div className="flex h-8 w-8 items-center justify-center rounded-full bg-destructive/10 text-destructive">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="16"
								height="16"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<title>Error icon</title>
								<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
								<line x1="12" y1="9" x2="12" y2="13" />
								<line x1="12" y1="17" x2="12.01" y2="17" />
							</svg>
						</div>
						<div>
							<h2 className="text-sm font-medium text-destructive">
								{presented?.title ?? "Something went wrong"}
							</h2>
							<p className="mt-1 text-xs text-destructive/80">
								{presented?.description ??
									this.state.error?.message ??
									"An unexpected error occurred"}
							</p>
							<Button
								className="mt-3"
								size="sm"
								variant="destructive"
								onClick={() => this.setState({ hasError: false, error: null })}
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="14"
									height="14"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
									className="mr-1"
								>
									<title>Reset icon</title>
									<path d="M21 2v6h-6"></path>
									<path d="M3 12a9 9 0 0 1 15-6.7L21 8"></path>
									<path d="M3 22v-6h6"></path>
									<path d="M21 12a9 9 0 0 1-15 6.7L3 16"></path>
								</svg>
								Try again
							</Button>
						</div>
					</div>
				</div>
			);
		}

		return this.props.children;
	}
}

const withErrorBoundary =
	<P extends {}>(Component: React.ComponentType<P>) =>
	(props: P) => (
		<ErrorBoundary>
			<Component {...props} />
		</ErrorBoundary>
	);

export { ErrorBoundary, withErrorBoundary };
